import { supabase } from '@/lib/supabase/client'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export const NOTIFICATION_CATEGORIES = ['billing', 'support', 'growth'] as const
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number]

export type NotificationSeverity = 'info' | 'success' | 'warning' | 'critical'

export interface AdminNotification {
  id: string
  type: string
  category: NotificationCategory
  severity: NotificationSeverity
  title: string
  body: string | null
  target_type: string | null
  target_id: string | null
  actor_user_id: string | null
  metadata: Record<string, unknown>
  event_at: string
  is_read: boolean
}

export interface NotificationListParams {
  page?: number
  pageSize?: number
  category?: NotificationCategory | null
  unreadOnly?: boolean
}

export interface NotificationListResult {
  notifications: AdminNotification[]
  total: number
  page: number
  pageSize: number
}

export interface UnreadCountResult {
  total: number
  byCategory: Record<NotificationCategory, number>
}

export interface NotificationPreferences {
  categories: Record<NotificationCategory, boolean>
  muted_types: string[]
}

// ---------------------------------------------------------------------------
// Edge function invocation helper
// ---------------------------------------------------------------------------

async function invokeAdminNotifications<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('admin-notifications', { body })
  if (error) throw new Error(String(error))
  if (data?.error) throw new Error(data.error)
  return data as T
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export function fetchNotifications(params: NotificationListParams = {}) {
  return invokeAdminNotifications<NotificationListResult>({ action: 'list', ...params })
}

export function fetchNotificationUnreadCount() {
  return invokeAdminNotifications<UnreadCountResult>({ action: 'unread_count' })
}

export function markNotificationsRead(ids: string[]) {
  return invokeAdminNotifications<{ marked: number }>({ action: 'mark_read', ids })
}

export function markAllNotificationsRead(category?: NotificationCategory | null) {
  return invokeAdminNotifications<{ success: boolean }>({
    action: 'mark_all_read',
    ...(category ? { category } : {}),
  })
}

export function fetchNotificationPreferences() {
  return invokeAdminNotifications<{ preferences: NotificationPreferences }>({
    action: 'get_preferences',
  })
}

export function saveNotificationPreferences(preferences: NotificationPreferences) {
  return invokeAdminNotifications<{ preferences: NotificationPreferences }>({
    action: 'set_preferences',
    preferences,
  })
}
